import React from 'react';
import { Divider, Typography, styled } from '@mui/material';
import {
  numberToCurrencyString,
  CalendarDay,
  pluralize,
  numberToPercentageString,
} from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { PartnerTransferSection } from 'further-types/lp/partner-transfer';
import EqualisationsAllocationsTable from 'components/EqualisationElements/EqualisationsAllocationsTable';
import { AllocationRule } from 'further-types/lp/allocations';
import { sumBy } from 'lodash';
import { ActionRefLabels } from 'further-ui/labels';
import { Allocations } from 'components/AllocationsTable/types';
import RealisationAllocationsTable from './RealisationAllocationsTable';
import { ActionRef } from 'further-types/lp/action';
import { sortAllocations } from '../utils/sortAllocations';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import TabsPanel from 'further-ui/components/TabsPanel';
import { Column } from 'further-ui/components/Layout';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import AvatarWithName from 'components/new/AvatarWithName';

const ContentColumn = styled(Column)(({ theme }) => ({
  padding: theme.spacing(
    0,
    theme.spacingSizes.sm,
    theme.spacingSizes.sm,
    theme.spacingSizes.sm,
  ),
}));

type PartnerTransferAllocation = Omit<
  Allocations,
  'allocationAmountTcy' | 'equalisationId' | 'closeDate'
> & { closeDate: CalendarDay };

interface RealisationAllocation {
  holdingClassId: string;
  commitmentId: string;
  investmentId: string;
  investorLabel: string;
  allocationPercentage: number;
  originalAllocationPercentage: number;
  allocationType: AllocationRule;
  receivedConsiderationLcy: number;
  earnoutConsiderationLcy: number;
  escrowConsiderationLcy: number;
  withholdingTaxPayableLcy: number;
  withholdingTaxExempt: boolean;
  unitsAllocated: number;
  excludeFromAllocation?: boolean;
}

interface LinkedAction {
  counterpartyLabel: string;
  actionRef: ActionRef;
  allocationRule: AllocationRule;
  actionId: string;
  ledgerDate: CalendarDay;
  totalAmountLcy: number;
  originalAllocations?: Array<PartnerTransferAllocation>;
  newAllocations: Array<PartnerTransferAllocation>;
  originalRealisationAllocations?: Array<RealisationAllocation>;
  newRealisationAllocations?: Array<RealisationAllocation>;
  newRealisationAllocationTotals: {
    totalReceivedConsiderationLcy: number;
    totalEarnoutLcy: number;
    totalEscrowLcy: number;
    totalWithholdingTaxLcy: number;
    totalUnits: number;
  };
  actionType?: string;
}

interface ActionCardProps {
  action: {
    counterpartyLabel: string;
    actionType?: string;
    allocationRule: AllocationRule;
    actionRef?: ActionRef;
    actionId: string;
    ledgerDate: CalendarDay;
    totalAmountLcy: number;
    originalAllocations?: Array<PartnerTransferAllocation>;
    newAllocations?: Array<PartnerTransferAllocation>;
    originalRealisationAllocations?: Array<RealisationAllocation>;
    newRealisationAllocations?: Array<RealisationAllocation>;
    linkedActions?: Array<LinkedAction>;
  };
  actionIndex: number;
  baseCurrency: CurrencyCode;
  isLastElement?: boolean;
  partnerTransferSection: PartnerTransferSection;
  transferDate?: CalendarDay;
}

const ActionCard: React.FC<ActionCardProps> = ({
  action,
  actionIndex,
  baseCurrency,
  partnerTransferSection,
  transferDate,
}) => {
  const { expandedRows, toggleRowExpansion } = useExpandedRows();

  const linkedActions = (action.linkedActions ?? []).map((linkedAction) => ({
    id: linkedAction.actionId,
    ...linkedAction,
    actionType:
      linkedAction.actionType ??
      (linkedAction.actionRef ? ActionRefLabels[linkedAction.actionRef] : ''),
  }));

  const linkedActionColumns: Array<
    TableColumn<(typeof linkedActions)[number]>
  > = [
    {
      key: 'investor',
      label: 'Investor',
      render: (row) => <AvatarWithName name={row.counterpartyLabel} />,
    },
    {
      key: 'type',
      label: 'Type',
      render: (row) => row.actionType,
    },
    {
      key: 'date',
      label: 'Date',
      render: (row) => row.ledgerDate.toReadable(),
    },
    {
      key: 'amount',
      label: 'Amount',
      render: (row) =>
        numberToCurrencyString(row.totalAmountLcy, { currency: baseCurrency }),
    },
  ];

  const renderAllocationsTable = (
    allocations: Array<PartnerTransferAllocation>,
  ) => {
    if (!allocations || allocations.length === 0) {
      return <Typography variant="body2">No allocations</Typography>;
    }

    const sortedAllocations = sortAllocations(allocations);

    const rows = sortedAllocations.map((allocation, idx) => ({
      id: idx.toString(),
      ...allocation,
    }));

    type Row = (typeof rows)[number];

    const columns: Array<TableColumn<Row>> = [
      {
        key: 'investor',
        label: 'Investor',
        width: '250px',
        render: (row) => row.investorLabel,
        footer: `Total from ${
          allocations.filter((a) => !a.excludeFromAllocation).length
        } ${pluralize(
          'investor',
          allocations.filter((a) => !a.excludeFromAllocation).length,
        )}`,
      },
      {
        key: 'totalCommitmentLcy',
        label: 'Total commitment',
        width: '200px',
        render: (row) =>
          row.totalCommitmentLcy
            ? numberToCurrencyString(row.totalCommitmentLcy, {
                currency: baseCurrency,
              })
            : '-',
      },
      {
        key: 'unallocatedCommitmentLcy',
        label: 'Unallocated commitment',
        width: '200px',
        render: (row) =>
          row.unallocatedCommitmentLcy
            ? numberToCurrencyString(row.unallocatedCommitmentLcy, {
                currency: baseCurrency,
              })
            : '-',
      },
      {
        key: 'closeDate',
        label: 'Close date',
        width: '150px',
        render: (row) => row.closeDate.toReadable(),
      },
      {
        key: 'allocationAmountLcy',
        label: 'Allocation allocated',
        width: '180px',
        render: (row) =>
          numberToCurrencyString(row.allocationAmountLcy, {
            currency: baseCurrency,
          }),
        footer: numberToCurrencyString(
          sumBy(
            allocations.filter((a) => !a.excludeFromAllocation),
            'allocationAmountLcy',
          ),
          { currency: baseCurrency },
        ),
      },
      {
        key: 'allocationPercentage',
        label: 'Allocation allocated (%)',
        width: '200px',
        render: (row) => `${row.allocationPercentage.toFixed(2)}%`,
        footer: numberToPercentageString(
          sumBy(
            allocations.filter((a) => !a.excludeFromAllocation),
            'allocationPercentage',
          ),
          {
            noMultiply: true,
            accuracy: 2,
            forceDecimals: true,
          },
        ),
      },
    ];

    const allocationRuleOptions = [
      { value: AllocationRule.Manual, label: 'Manual' },
      {
        value: AllocationRule.ByCommitmentAndClosingDate,
        label: 'By Commitment and Closing Date',
      },
      { value: AllocationRule.LinkedAction, label: 'Linked Action' },
      {
        value: AllocationRule.PerOriginalInvestment,
        label: 'Per Original Investment',
      },
    ];

    return (
      <EqualisationsAllocationsTable
        columns={columns}
        rows={rows}
        showAllocationRule={true}
        checkboxes={{
          isCheckedRow: (row) => !row.excludeFromAllocation,
          isDisabledRow: () => true,
          onRowCheckedToggle: () => {},
        }}
        allocationRule={action.allocationRule}
        allocationRuleOptions={allocationRuleOptions}
        isNotEditable={true}
      />
    );
  };

  return (
    <>
      <Divider />
      <ContentColumn spacing="lg">
        <TabsPanel
          defaultTab="after"
          tabs={[
            {
              key: 'after',
              label: 'New allocation',
              content:
                action.newRealisationAllocations &&
                action.newRealisationAllocations.length > 0 ? (
                  <RealisationAllocationsTable
                    realisationAllocations={sortAllocations(
                      action.newRealisationAllocations,
                    )}
                    baseCurrency={baseCurrency}
                    allocationRule={action.allocationRule}
                  />
                ) : action.newAllocations &&
                  action.newAllocations.length > 0 ? (
                  renderAllocationsTable(action.newAllocations)
                ) : (
                  <Typography variant="body2">No allocations</Typography>
                ),
            },
            {
              key: 'before',
              label: 'Original allocation',
              content:
                action.originalRealisationAllocations &&
                action.originalRealisationAllocations.length > 0 ? (
                  <RealisationAllocationsTable
                    realisationAllocations={sortAllocations(
                      action.originalRealisationAllocations,
                    )}
                    baseCurrency={baseCurrency}
                    allocationRule={action.allocationRule}
                  />
                ) : action.originalAllocations &&
                  action.originalAllocations.length > 0 ? (
                  renderAllocationsTable(action.originalAllocations)
                ) : (
                  <Typography variant="body2">No allocations</Typography>
                ),
            },
          ]}
        />
        {linkedActions.length > 0 ? (
          <Column spacing="sm">
            <Typography variant="h6" color="grey.600">
              Linked actions
            </Typography>
            <Table
              showTopBorder
              showHeaderBorder
              isExpandedRow={(row) => expandedRows.has(row.id)}
              onRowClick={(row) => toggleRowExpansion(row.id)}
              onRowExpand={(row) => toggleRowExpansion(row.id)}
              isExpandableRow={() => true}
              getExpandedContent={(row, _width, rowIndex) => (
                <ActionCard
                  key={rowIndex}
                  action={{ ...row, linkedActions: [] }}
                  actionIndex={actionIndex}
                  baseCurrency={baseCurrency}
                  partnerTransferSection={partnerTransferSection}
                  transferDate={transferDate}
                />
              )}
              rows={linkedActions}
              columns={linkedActionColumns}
            />
          </Column>
        ) : null}
      </ContentColumn>
    </>
  );
};

export default ActionCard;
